import {
    microsoftAuthApiRef,
    useApi
} from '@backstage/core-plugin-api';
import { useState, useCallback } from 'react'
import { useAsync } from 'react-use';
/** Decode a JWT payload without any Node.js built-ins (browser-compatible). */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(json);
}

export const useOauthSignIn = ({ scopes }: { scopes: string[] }) => {
    if (scopes.length === 0) {
        throw new Error('Scopes are required');
    }

    const authApi = useApi(microsoftAuthApiRef);
    const [isSignedIn, setSignedIn] = useState(false);
    const [token, setToken] = useState('');
    const [forceLogin, setForceLogin] = useState(false);

    const createSessionValue = (token: string) => {
        return JSON.stringify({
            scopes: scopes,
            token: token,
        });
    }

    const doSignIn = useCallback(
        async (usePopup = true) => {
            const completeScopes = [...scopes, 'email', 'openid'];
            const accessToken = await authApi.getAccessToken(completeScopes, {
                optional: !usePopup,
                instantPopup: usePopup,
            });
            if (accessToken) {
                setSignedIn(!!accessToken);
                setToken(accessToken);
                sessionStorage.setItem('azureDevOpsToken', createSessionValue(accessToken));
            }
        },
        [authApi, setSignedIn, scopes]
    );

    const { loading, error } = useAsync(async () => {
        const sessionValue = sessionStorage.getItem('azureDevOpsToken');
        const value: { scopes: string[], token: string } | undefined = sessionValue ? JSON.parse(sessionValue) : undefined;
        if (value) {
            if (!scopes.every(scope => value.scopes.includes(scope))) {
                sessionStorage.removeItem('azureDevOpsToken');
                setToken('');
                setSignedIn(false);
                return;
            }

            const decodedToken = decodeJwtPayload(value.token);
            const fiveMinutesInMs = 5 * 60 * 1000;
            if (typeof decodedToken.exp === 'number' && decodedToken.exp < (Date.now() - fiveMinutesInMs) / 1000) {
                sessionStorage.removeItem('azureDevOpsToken');
                setToken('');
                setSignedIn(false);
                return;
            }

            setToken(value.token);
            setSignedIn(true);
        }

        if (!isSignedIn) {
            await doSignIn(forceLogin);
        }
    }, [forceLogin, isSignedIn]);

    const showSignInModal = useCallback(() => {
        setForceLogin(true);
    }, [setForceLogin]);

    return { token, loading, error, showSignInModal, isSignedIn };
};
